import Header from '../components/Header';
import Footer from '../components/Footer';
import About from '../components/About';
import Guides from '../components/Guides';
import FAQ from '../components/FAQ';
import News from '../components/News';
import Contact from '../components/Contact';
import { ExternalLink, Sparkles, Star, Gift } from 'lucide-react';

const Slots = () => {
  // Reordered based on slots quality - NRG and Mr.Play have free spins, better for slots
  const slotsOffers = [
    {
      id: 2,
      name: 'NRG',
      url: 'https://nrg.bet/',
      bonus: '200% on first deposit + 100 free spins',
      description: 'Triple your money and spin 100 times on the house. NRG delivers high-energy gaming with one of the most generous welcome packages in the UK, perfect for slot enthusiasts seeking maximum spins and excitement.',
      icon: <Sparkles className="w-8 h-8" />,
      color: 'from-orange-600 to-red-600'
    },
    {
      id: 3,
      name: 'Mr.Play',
      url: 'https://www.mrplay.com/',
      bonus: '100 spins + 100% up to £200 bonus',
      description: 'Get 100 free spins plus match your deposit up to £200. Mr.Play offers premium slot entertainment with a welcome package that combines instant spins with substantial bonus funds for extended play.',
      icon: <Star className="w-8 h-8" />,
      color: 'from-purple-600 to-red-600'
    },
    {
      id: 1,
      name: 'PUB Casino',
      url: 'https://www.pubcasino.co.uk/',
      bonus: '100% up to £100',
      description: 'Double your first deposit and start spinning with extra credit. PUB Casino combines classic pub atmosphere with modern slot gaming, offering a solid bonus foundation for your slot adventures.',
      icon: <Gift className="w-8 h-8" />,
      color: 'from-red-600 to-red-800'
    }
  ];

  return (
    <div className="min-h-screen bg-regal-dark">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-regal-dark via-regal-gray to-regal-dark py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("/images/chris-liverani-2y7yiuEgT3k-unsplash.webp")', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white leading-tight">
              Best UK Slot Casinos
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Spin your way to excitement with the UK's premier slot casinos. We've handpicked platforms offering the finest slot selections, generous free spin bonuses, and entertainment that keeps you coming back for more.
            </p>
          </div>
        </div>
      </section>

      {/* Slots Offers Section */}
      <section className="py-20 bg-regal-dark">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              Top Slot Casino Offers
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              These casinos excel at slot gaming, offering extensive game libraries, free spin bonuses, and experiences tailored specifically for slot enthusiasts who demand variety and value.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {slotsOffers.map((offer, index) => (
              <div
                key={offer.id}
                className={`bg-regal-gray rounded-xl overflow-hidden border-2 border-gray-800 hover:border-regal-red transition-all transform hover:scale-105 ${
                  index === 0 ? 'md:scale-105 md:border-regal-red' : ''
                }`}
              >
                <div className={`bg-gradient-to-r ${offer.color} p-6 flex items-center justify-between`}>
                  <div className="text-white">
                    {offer.icon}
                  </div>
                  <span className="text-white font-bold text-lg">{offer.name}</span>
                </div>
                
                <div className="p-6">
                  <div className="mb-4">
                    <span className="text-regal-red font-bold text-2xl">{offer.bonus}</span>
                  </div>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {offer.description}
                  </p>
                  <a
                    href={offer.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow sponsored"
                    className="w-full bg-regal-red hover:bg-red-700 text-white py-3 px-6 rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
                  >
                    Play at {offer.name}
                    <ExternalLink size={18} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Slots Matter Section */}
      <section className="py-20 bg-regal-gray">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                Why Slots Dominate UK Online Casinos
              </h2>
            </div>

            <div className="space-y-8 text-gray-300 leading-relaxed text-lg">
              <p>
                Slots aren't just popular—they're the heartbeat of UK online casino gaming. From classic three-reel fruit machines to cutting-edge video slots with thousands of ways to win, slot games offer something for every player. The appeal is undeniable: instant action, massive jackpots, and the thrill of watching reels align for potentially life-changing wins.
              </p>
              <p>
                When choosing slot casinos, game variety matters tremendously. Top platforms offer hundreds, sometimes thousands, of slot titles from leading providers like NetEnt, Microgaming, Play'n GO, and Pragmatic Play. This variety ensures you'll never run out of fresh themes, innovative mechanics, or exciting bonus features to explore. Whether you prefer adventure-themed slots, mythology-inspired games, or classic fruit symbols, premier slot casinos deliver extensive catalogs.
              </p>
              <p>
                Free spins bonuses are slot players' best friends. These bonuses let you experience new games risk-free while maintaining winning potential. The best slot casinos offer welcome packages combining free spins with deposit matches, creating comprehensive value that extends playtime and maximizes opportunities. Understanding which casinos offer genuine free spin value—not just marketing promises—helps you choose platforms that truly reward slot enthusiasts.
              </p>
              <p>
                RTP percentages reveal slot value beyond flashy graphics and themes. Reputable slot casinos display return-to-player rates clearly, allowing informed game selections. While individual sessions vary wildly due to randomness, higher RTP slots provide better long-term value. Top slot casinos ensure games meet fairness standards through independent testing and transparent RTP disclosure.
              </p>
              <p>
                Progressive jackpot slots offer life-changing win potential that attracts millions of players worldwide. These games pool contributions across multiple casinos, creating prize pools reaching millions. While hitting progressive jackpots requires extraordinary luck, the potential rewards make these slots incredibly appealing. Premier slot casinos offer extensive progressive jackpot selections, giving you multiple chances at transformational wins.
              </p>
              <p>
                Mobile slot gaming has revolutionized how UK players enjoy casino entertainment. Modern slot casinos optimize games for smartphones and tablets, ensuring smooth gameplay, stunning graphics, and full feature access on mobile devices. You can spin reels during commutes, lunch breaks, or anywhere with internet access. This accessibility explains slots' dominance—convenience meets excitement in perfect harmony.
              </p>
              <p>
                Bonus features transform slot gaming from simple spinning into engaging experiences. Free spin rounds, pick-and-win bonuses, cascading reels, and multiplier systems create variety that maintains excitement across extended sessions. The best slot casinos partner with providers known for innovative features, ensuring games offer more than basic reels and paylines.
              </p>
              <p>
                Volatility matters when selecting slot games that match your playing style. Low volatility slots pay smaller amounts more frequently, providing steady entertainment. High volatility slots pay less often but deliver larger wins when they hit, creating more dramatic excitement. Medium volatility balances these approaches. Understanding volatility helps you choose slots aligned with bankroll size and risk tolerance.
              </p>
              <p>
                Slot casinos that excel understand player preferences and deliver experiences tailored to slot enthusiasts. They offer extensive game libraries, generous free spin bonuses, mobile optimization, and transparent terms that enable informed decisions. These platforms recognize that slot players value variety, value, and entertainment—and they deliver on all fronts. When you find slot casinos that prioritize these elements, you've found platforms worth your time and deposits.
              </p>
            </div>
          </div>
        </div>
      </section>

      <About />
      <Guides />
      <FAQ />
      <News />
      <Contact />
      <Footer />
    </div>
  );
};

export default Slots;

